import React, {
  useEffect, useMemo, useRef, useState,
} from 'react';
import { Field, FieldTypes, useDebounce } from '@common-fe/common-fe';

import { REQUIRED_TEXT } from '@/common/constants';
import { OverridablePropertiesDto } from '@/modules/employer/types';

import FraudThresholdField from '../components/FraudThresholdField';
import useFraudThresholdStore from '../stores/useFraudThreshold.store';

const POSITIVE_NUMBER_TEXT = 'Should be a positive number';

interface ThresholdConfig {
  checkboxKey: 'isFraudSingleClaimThresholdEnabled' | 'isFraudDailyThresholdEnabled' | 'isFraudWeeklyThresholdEnabled';
  amountKey: 'fraudSingleClaimThresholdAmount' | 'fraudDailyThresholdAmount' | 'fraudWeeklyThresholdAmount';
  title: string;
  desc: string;
  testId: string;
}

const THRESHOLDS: ThresholdConfig[] = [
  {
    checkboxKey: 'isFraudSingleClaimThresholdEnabled',
    amountKey: 'fraudSingleClaimThresholdAmount',
    title: 'Single claim',
    desc: 'Flag employee when an individual claim exceeds the configured threshold amount',
    testId: 'fraud-threshold_single-claim-threshold',
  },
  {
    checkboxKey: 'isFraudDailyThresholdEnabled',
    amountKey: 'fraudDailyThresholdAmount',
    title: 'Daily',
    desc: "Flag employee when the participant's total reimbursements exceed the configured threshold amount within a rolling 24-hour period",
    testId: 'fraud-threshold_daily-threshold',
  },
  {
    checkboxKey: 'isFraudWeeklyThresholdEnabled',
    amountKey: 'fraudWeeklyThresholdAmount',
    title: 'Weekly',
    desc: "Flag employee when the participant's total reimbursements exceed the configured threshold amount within a rolling 7-day period",
    testId: 'fraud-threshold_weekly-threshold',
  },
];

const useThresholdField = (
  config: ThresholdConfig,
  overridableProperties?: OverridablePropertiesDto,
  disabled?: boolean,
  showError?: boolean,
  viewMode?: boolean,
  isVisible?: boolean,
): Field => {
  const { setFraudThreshold } = useFraudThresholdStore();
  const [checked, setChecked] = useState(!!overridableProperties?.[config.checkboxKey]);
  const [amount, setAmount] = useState(overridableProperties?.[config.amountKey] || '');
  const debouncedAmount = useDebounce(amount);
  const hasSyncedFromServer = useRef(false);

  useEffect(() => {
    if (hasSyncedFromServer.current || overridableProperties === undefined) return;
    hasSyncedFromServer.current = true;
    setChecked(!!overridableProperties[config.checkboxKey]);
    setAmount(overridableProperties[config.amountKey] || '');
  }, [overridableProperties, config]);

  useEffect(() => {
    if (!isVisible) return;
    setFraudThreshold({
      [config.checkboxKey]: checked,
      [config.amountKey]: debouncedAmount,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checked, debouncedAmount, setFraudThreshold, isVisible]);

  const error = useMemo(() => {
    if (!showError || !checked) return undefined;
    if (!amount) return REQUIRED_TEXT;
    if (Number(amount) <= 0) return POSITIVE_NUMBER_TEXT;
    return undefined;
  }, [showError, checked, amount]);

  return useMemo<Field>(() => ({
    name: config.checkboxKey,
    type: FieldTypes.Node,
    fill: true,
    fieldWrapperStyles: { marginBottom: '16px' },
    value: (
      <FraudThresholdField
        title={config.title}
        desc={config.desc}
        testId={config.testId}
        disabled={disabled}
        viewMode={viewMode}
        checked={checked}
        amount={amount}
        error={error}
        onCheckedChange={setChecked}
        onAmountChange={setAmount}
      />
    ),
  }), [config, disabled, viewMode, checked, amount, error]);
};

const useFraudThresholdFields = (
  overridableProperties?: OverridablePropertiesDto,
  disabled?: boolean,
  showError?: boolean,
  viewMode?: boolean,
  isVisible?: boolean,
): Field[] => {
  const singleClaimField = useThresholdField(
    THRESHOLDS[0], overridableProperties, disabled, showError, viewMode, isVisible
  );
  const dailyField = useThresholdField(
    THRESHOLDS[1], overridableProperties, disabled, showError, viewMode, isVisible
  );
  const weeklyField = useThresholdField(
    THRESHOLDS[2], overridableProperties, disabled, showError, viewMode, isVisible
  );

  return useMemo(
    () => [
      singleClaimField,
      dailyField,
      { ...weeklyField, hasHorizontalLine: true, fieldWrapperStyles: { marginBottom: '16px' } },
    ],
    [singleClaimField, dailyField, weeklyField],
  );
};

export default useFraudThresholdFields;
